import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import authenticationService from 'auth/services/authenticationService'
import { getReturnUrl } from 'auth/utils/authHelpers'
import {
	CircleSpinner,
	ErrorMessage,
	getCountryA3ForCurrentMarket,
	validateOtpCode,
} from 'shared'
import { AxiosError } from 'axios'
import { Form, FormControl } from 'react-bootstrap'
import { formatSsn, validateSsn } from 'original/utils/validation.helper'
import { CountdownTimer } from 'shared/utils/dateAndTimeHelpers'
import { IOtpStartResponse } from 'auth/interfaces/i-otpid'
import './balticOtpContentBlock.scss'
import TermsAndConditionBlock from 'auth/constants/termsAndConditions/termsAndConditionsBlock'

const BalticOtpContentBlock = () => {
	const { t } = useTranslation()
	const [isDone, setIsDone] = useState<boolean>(false)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [error, setError] = useState<Error | undefined>()

	const [otpData, setOtpData] = useState<IOtpStartResponse>()
	const [otpTimedOut, setTimeOut] = useState<boolean>(false)
	const [otpCode, setOtpCode] = useState<string>('')
	const [otpCodeIsValid, setOtpCodeIsValid] = useState<boolean>(true)

	const [ssn, setSsn] = useState<string>('')
	const [ssnIsValid, setSsnIsValid] = useState<boolean>(true)

	const resetStates = () => {
		setIsLoading(false)
		setError(undefined)
		setOtpData(undefined)
		setOtpCode('')
		setSsn('')
		setSsnIsValid(true)
		setTimeOut(false)
		setIsDone(false)
	}

	function onSsnChange(e: React.FormEvent<any>) {
		const value = formatSsn(e.currentTarget.value as string, ssn)
		setSsn(value || '')
	}

	const loginStart = async () => {
		setError(undefined)
		setIsLoading(true)

		try {
			if (!validateSsn(ssn)) {
				setSsnIsValid(false)
				return
			}

			const country = getCountryA3ForCurrentMarket()
			if (!country)
				throw new Error('This country is not currently supported')

			const data = await authenticationService.otpLoanStart(
				{
					ssn: ssn,
					country: country,
				},
				t,
			)
			setOtpData(data)
		} catch (e) {
			if ((e as AxiosError).response?.status === 400) {
				setError(new Error(t('OtpBadRequestError')))
			} else {
				setError(new Error(t('OtpInternalServerError')))
			}
		} finally {
			setIsLoading(false)
		}
	}

	const loginComplete = async () => {
		setError(undefined)
		setIsLoading(true)

		try {
			if (!validateOtpCode(otpCode)) {
				setOtpCodeIsValid(false)
				return
			}

			const response = await authenticationService.otpComplete(
				{
					secret: otpData?.secret!,
					otp: otpCode,
					returnUrl: getReturnUrl(),
				},
				t,
			)

			setIsDone(true)

			authenticationService.redirectTo(
				response.redirectUrl || getReturnUrl(),
				response.secret,
			)
		} catch (e) {
			if ((e as AxiosError).response?.status === 400) {
				setError(new Error(t('OtpBadRequestError')))
			} else {
				setError(new Error(t('OtpInternalServerError')))
			}
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className="baltic-otp-container">
			{isLoading || isDone ? (
				<CircleSpinner />
			) : otpTimedOut ? (
				<>
					<div>{t('OtpGoneError')}</div>
					<button
						onClick={() => resetStates()}
						className="btn btn-primary mt-4">
						{t('Restart')}
					</button>
				</>
			) : (
				<>
					{otpData?.secret ? (
						<>
							<div className="login-description mb-4">
								<div>{t('OtpEnterCodeInfo')}</div>
								<div className="otp-timer-container">
									<Trans
										t={t}
										i18nKey="OtpCodeValidTo"
										components={[
											<div className="otp-timer">
												<CountdownTimer
													isTimedOut={(
														bool: boolean,
													) => {
														setTimeOut(bool)
													}}
													time={{
														expiryDate:
															otpData?.expiryAtUtc,
													}}
													format={`mm:ss`}
												/>
											</div>,
										]}
									/>
								</div>
							</div>
							<FormControl
								placeholder={t('OTPCode')}
								className="mt-2"
								name="otp"
								id="otp"
								onChange={(e) => setOtpCode(e.target.value)}
								onBlur={() => {
									setOtpCodeIsValid(validateOtpCode(otpCode))
								}}
								isInvalid={!otpCodeIsValid}
								defaultValue={otpCode}
							/>
							<Form.Control.Feedback type="invalid">
								{t('OtpFormatInvalid')}
							</Form.Control.Feedback>
						</>
					) : (
						<>
							<div className="login-description mb-4">
								{t('OtpEnterSsnToInit')}
							</div>
							<FormControl
								placeholder={t('SSN')}
								className="mt-2"
								name="ssn"
								id="ssn"
								onChange={onSsnChange}
								onBlur={() => setSsnIsValid(validateSsn(ssn))}
								isInvalid={!ssnIsValid}
								value={ssn}
							/>
							<Form.Control.Feedback type="invalid">
								{t('InvalidSsn')}
							</Form.Control.Feedback>
						</>
					)}
					{error && (
						<ErrorMessage message={error.message} css="mt-4" />
					)}
					<button
						onClick={!otpData ? loginStart : loginComplete}
						className="btn btn-primary mt-4">
						{t('Proceed')}
					</button>
					<TermsAndConditionBlock className="mt-4" />
				</>
			)}
		</div>
	)
}

export default BalticOtpContentBlock
